import React, { useEffect, useMemo } from 'react';
import { DialogModal, InfoModalProps } from '../../../../theme/components';
import { useDevicesControlUnits } from '../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import {
  FormGroup,
  FormLabel,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { getTranslationNameByData } from '../../../../redux/monitoring/api/util/getLabelName';
import { useCUsTranslate } from '../../../../redux/CUTranslation/hook/useCUsTranslate';
import { getPolyglot } from '../../../../i18n';
import { isNotNullOrUndefined } from '../../../../util/types/Array';
import {
  CHANNEL_TYPE,
  ChannelMap,
} from '../../../../redux/reports/api/Report.model';
import { useFormik } from 'formik';
import useChannelsFromComposableDevice from '../../../../redux/composableDevice/hooks/useChannelsFromComposableDevice';
const KEY_CONTROL_UNITS_BY_TYPE = 'channels';
interface EditChannelModalProps extends InfoModalProps {
  onSaveItem: (item: ChannelMap) => void;
  channel?: ChannelMap;
  groupId: string | undefined;
}
function EditChannelModal(props: EditChannelModalProps) {
  const { onSaveItem, channel, groupId, ...modalProps } = props;
  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();
  const [CUsTranslate, loadingTranslations] = useCUsTranslate();
  const polyglot = getPolyglot();
  const { data: composableChannels } = useChannelsFromComposableDevice(
    groupId ?? ''
  );
  const foundChannel = composableChannels.find(
    (ch) =>
      ch.channelID === channel?.channelID &&
      ch.cuID === channel?.cuID &&
      ch.deviceID === channel?.deviceID
  );

  const channelMetadata = useMemo(
    () =>
      devicesControlUnits
        .find(
          (devicesControlUnit) =>
            devicesControlUnit.deviceId === channel?.deviceID
        )
        ?.controlUnits?.find((controlUnit) => controlUnit.id === channel?.cuID)
        ?.channels.find((ch) => ch.id === channel?.channelID),
    [devicesControlUnits, channel]
  );

  const primaryText =
    foundChannel?.name ??
    getTranslationNameByData(
      channelMetadata?.name,
      channelMetadata?.id,
      CUsTranslate,
      channel?.deviceID,
      channel?.cuID
    );

  const longTranslation = CUsTranslate(
    channelMetadata?.name ?? channelMetadata?.id ?? '',
    channel?.deviceID,
    channel?.cuID,
    'ram',
    'longText',
    false,
    true
  ) as string;

  const secondaryText = foundChannel?.name
    ? undefined
    : [
        channelMetadata?.id
          ? `${polyglot.t('device.monitoring.id_taq')}: ${channelMetadata?.id}`
          : undefined,
        channelMetadata?.name
          ? `${polyglot.t('device.monitoring.name_taq')}: ${
              channelMetadata?.name
            }`
          : undefined,
        longTranslation
          ? `${polyglot.t(
              'device.monitoring.description_taq'
            )}: ${longTranslation}`
          : undefined,
      ]
        .filter(isNotNullOrUndefined)
        .join(', ');

  const formik = useFormik({
    initialValues:
      channel ||
      ({
        channelID: '',
        cuID: '',
        deviceID: '',
        maxThreshold: null,
        minThreshold: null,
        type: CHANNEL_TYPE.ANALOG,
      } as ChannelMap),
    enableReinitialize: true,
    onSubmit: (values) => {
      onSaveItem(values);
    },
  });

  useEffect(() => {
    formik.resetForm();
  }, [props.open]);

  return (
    <DialogModal {...modalProps} onConfirm={() => formik.handleSubmit()}>
      <Typography variant="h2" mb={1}>
        {polyglot.t('reports.channel_map.edit_channel')}
      </Typography>

      <Stack sx={{ width: '100%' }} spacing={2}>
        <Typography>
          {polyglot.t('reports.channel_map.edit_channel_helper_text')}
        </Typography>
        {loadingDevicesControlUnits && (
          <Skeleton variant="rectangular" width="560px" height={118} />
        )}
        <FormGroup>
          <FormLabel>{polyglot.t('reports.channel_map.channel')}</FormLabel>
          <ListItem dense>
            {loadingTranslations ? (
              <Skeleton width="100%" height={24} />
            ) : (
              <ListItemText
                primary={primaryText}
                secondary={secondaryText}
                primaryTypographyProps={{
                  sx: { wordBreak: 'break-all' },
                }}
                secondaryTypographyProps={{
                  sx: { wordBreak: 'break-all' },
                }}
              />
            )}
          </ListItem>
        </FormGroup>

        <TextField
          label={polyglot.t('reports.channel_map.minThreshold')}
          value={formik.values.minThreshold ?? null}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          type="number"
          name="minThreshold"
          id="minThreshold"
          fullWidth
        />
        <TextField
          label={polyglot.t('reports.channel_map.maxThreshold')}
          value={formik.values.maxThreshold ?? null}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          type="number"
          name="maxThreshold"
          id="maxThreshold"
          fullWidth
        />
      </Stack>
    </DialogModal>
  );
}

export default EditChannelModal;
